import { formatInTimeZone } from "date-fns-tz";
import { type ChangeEvent, useEffect, useId, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RemoveIcon from "@mui/icons-material/Remove";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import Dialog from "components/dialog";
import { DEFAULT_TIMEZONE } from "constants/timezones";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { useGetAmbientScribePurchaseSummaryQuery } from "services/paywall";
import type {
  AmbientScribePurchasePerson,
  PaywallFeature
} from "services/types/paywall.type";
import { formatMoney } from "utils/common";

import { PURCHASE_LICENSES_COPY as COPY } from "./ambient-scribe-purchase-placeholder.copy";
import {
  MAX_LICENSES,
  MIN_LICENSES,
  clampLicenseCount,
  clampLicenseCountOnBlur,
  isValidLicenseCount
} from "./license-count.util";

const TARGET_SIZE = 44;

const TABLE_HEAD_CELL_SX = {
  fontWeight: 700,
  color: neutral[800],
  background: neutral[50]
} as const;

function trialLabel(person: AmbientScribePurchasePerson, timezone: string) {
  const day = person.trial_ends_at
    ? formatInTimeZone(person.trial_ends_at, timezone, "MMM d")
    : undefined;
  if (person.in_trial) return COPY.inTrial(day);
  if (person.trialed) return COPY.trialEnded(day);
  return COPY.noTrial;
}

export interface PurchaseLicensesDialogProps {
  /** Its price is the fallback when the summary cannot be read. */
  readonly feature: PaywallFeature;
  /** Inside the recording widget's purchase dialog, which sits at `snackbar + 101`. */
  readonly nestedLayerZIndex?: number;
  /** From Continue until the page leaves for Stripe, or the purchase comes back. */
  readonly isPurchasing: boolean;
  /** Why the last Continue did not reach Stripe, shown in the dialog; null when it has not failed. */
  readonly purchaseError: string | null;
  readonly onClose: () => void;
  readonly onPurchase: (licenses: number) => void;
}

/**
 * "Purchase Suno Scribes" (SN-19868): how many licences to buy, and who gets them. Mounted only
 * while open, so every opening starts from a fresh summary and a fresh count.
 *
 * The licences go out in `license_order` — the same order the webhook assigns them in — so the
 * "After you pay" column follows the count as it changes. A practice still buys when the summary
 * cannot be read: one licence, no list, the feature's price.
 */
export default function PurchaseLicensesDialog({
  feature,
  nestedLayerZIndex,
  isPurchasing,
  purchaseError,
  onClose,
  onPurchase
}: PurchaseLicensesDialogProps) {
  const currentPractice = useAppSelector(selectCurrentPractice);
  const timezone = currentPractice?.timezone || DEFAULT_TIMEZONE;
  const countId = useId();
  const countErrorId = useId();
  const [openedAt] = useState(() => Date.now());

  // Asked again on every opening, so the list includes colleagues who started a trial since.
  const { data, isError, isFetching, fulfilledTimeStamp } =
    useGetAmbientScribePurchaseSummaryQuery(undefined, {
      refetchOnMountOrArgChange: true
    });
  // The screen behind the dialog prefetched the summary, and its cached copy is what the first
  // render sees: it may predate a colleague's trial. Only an answer from since the dialog opened
  // counts. RTK also keeps the last answer when a refetch fails, so a failure reads as none.
  const isFresh =
    fulfilledTimeStamp !== undefined && fulfilledTimeStamp >= openedAt;
  const summary = isError || !isFresh ? undefined : data;

  const [licenses, setLicenses] = useState(String(MIN_LICENSES));
  const hasEditedRef = useRef(false);
  const hasPrefilledRef = useRef(false);

  // Prefilled once, from the first summary seen. A slow answer never replaces a count the person
  // has already changed, and a later refetch never moves it either.
  useEffect(() => {
    if (!summary || hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;
    if (!hasEditedRef.current) {
      setLicenses(String(clampLicenseCount(summary.suggested_licenses)));
    }
  }, [summary]);

  const count = Number(licenses);
  const isValidCount = licenses !== "" && isValidLicenseCount(count);

  const changeLicenses = (value: string) => {
    hasEditedRef.current = true;
    setLicenses(value);
  };

  const handleCountChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Digits only, so "1e3" or "1.5" cannot be typed in the first place.
    if (/^\d*$/.test(event.target.value)) changeLicenses(event.target.value);
  };

  const step = (delta: number) =>
    changeLicenses(
      String(clampLicenseCount((isValidCount ? count : 0) + delta))
    );

  const unitPrice = Number(summary ? summary.unit_price : feature.price);
  const hasPrice = unitPrice > 0;
  const trialDays = summary?.trial_days ?? null;
  // With a trial the card is not charged today and the webhook gives the buyer the first licence,
  // as before this dialog, so there is no list to choose from.
  const people = summary && !trialDays ? summary.license_order : [];
  const licensed = isValidCount ? count : 0;

  const total =
    isValidCount && hasPrice
      ? formatMoney(count * unitPrice, 2)
      : COPY.totalUnavailable;

  let countVsPeople: string | null = null;
  if (people.length > 0 && isValidCount) {
    if (count < people.length) {
      countVsPeople = COPY.fewerThanPeople(people.length - count);
    } else if (count > people.length) {
      countVsPeople = COPY.moreThanPeople(count - people.length);
    }
  }

  // Not while the payment page is opening: the call is in flight and the page may be leaving.
  const close = () => {
    if (!isPurchasing) onClose();
  };

  return (
    <Dialog
      open
      title={COPY.title}
      onClose={close}
      minWidth={760}
      disableDismissOnClickAway
      {...(nestedLayerZIndex !== undefined && {
        sx: { zIndex: nestedLayerZIndex }
      })}
      actions={
        <>
          <Button
            onClick={close}
            disabled={isPurchasing}
            sx={{ minHeight: TARGET_SIZE }}
          >
            {COPY.cancel}
          </Button>
          {/* Held while the summary loads: the list and the price it shows are what is bought.
              It loads once on opening and again after a 409, and a failed read releases it. */}
          <LoadingButton
            variant="contained"
            loading={isPurchasing}
            disabled={!isValidCount || isFetching}
            endIcon={<NavigateNextIcon />}
            onClick={() => onPurchase(count)}
            sx={{ minHeight: TARGET_SIZE }}
          >
            {COPY.continue}
          </LoadingButton>
        </>
      }
    >
      <Stack sx={{ gap: 3, pt: 1 }} data-testid="purchase-licenses-dialog">
        <Box>
          <Typography variant="body3" component="p" sx={{ fontWeight: 700 }}>
            {COPY.question}
          </Typography>
          <Typography variant="body2" sx={{ color: neutral[800] }}>
            {COPY.questionHint}
          </Typography>
        </Box>

        <Box>
          <Stack
            direction="row"
            useFlexGap
            sx={{ alignItems: "center", gap: 2, flexWrap: "wrap" }}
          >
            <Typography
              component="label"
              htmlFor={countId}
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              {COPY.countLabel}
            </Typography>
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <IconButton
                aria-label={COPY.decrease}
                onClick={() => step(-1)}
                disabled={isPurchasing || (isValidCount && count <= MIN_LICENSES)}
                sx={{
                  width: TARGET_SIZE,
                  height: TARGET_SIZE,
                  border: `1px solid ${neutral[400]}`
                }}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                id={countId}
                value={licenses}
                onChange={handleCountChange}
                onBlur={() => setLicenses(clampLicenseCountOnBlur(licenses))}
                disabled={isPurchasing}
                error={!isValidCount}
                inputProps={{
                  inputMode: "numeric",
                  "aria-describedby": isValidCount ? undefined : countErrorId
                }}
                sx={{
                  width: 96,
                  "& .MuiOutlinedInput-root": { height: TARGET_SIZE },
                  "& input": { textAlign: "center", fontWeight: 700 }
                }}
              />
              <IconButton
                aria-label={COPY.increase}
                onClick={() => step(1)}
                disabled={isPurchasing || (isValidCount && count >= MAX_LICENSES)}
                sx={{
                  width: TARGET_SIZE,
                  height: TARGET_SIZE,
                  border: `1px solid ${neutral[400]}`
                }}
              >
                <AddIcon />
              </IconButton>
            </Stack>
          </Stack>
          {!isValidCount && (
            <Typography
              id={countErrorId}
              variant="body1"
              sx={{ mt: 1, color: "error.main" }}
            >
              {COPY.invalidCount}
            </Typography>
          )}
        </Box>

        {people.length > 0 && (
          <Box>
            <Typography
              variant="body2"
              component="h3"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {COPY.peopleHeading}
            </Typography>
            <Table
              aria-label={COPY.peopleHeading}
              sx={{ border: `1px solid ${neutral[300]}` }}
            >
              <TableHead>
                <TableRow>
                  {[
                    COPY.columnName,
                    COPY.columnRole,
                    COPY.columnTrial,
                    COPY.columnAfterPay
                  ].map((column) => (
                    <TableCell key={column} sx={TABLE_HEAD_CELL_SX}>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {column}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {people.map((person, index) => {
                  const getsLicense = index < licensed;
                  return (
                    <TableRow key={person.user.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {person.user.name}
                          {person.is_you && ` ${COPY.you}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {person.user.role_label}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {trialLabel(person, timezone)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: "center",
                            gap: 0.5,
                            color: getsLicense ? misc.sunoGreen : neutral[800]
                          }}
                        >
                          {getsLicense && <CheckIcon aria-hidden />}
                          <Typography
                            variant="body2"
                            sx={{
                              color: "inherit",
                              fontWeight: getsLicense ? 700 : 400
                            }}
                          >
                            {getsLicense ? COPY.getsLicense : COPY.noLicense}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="body1" sx={{ mt: 1, color: neutral[800] }}>
              {COPY.orderNote}
            </Typography>
          </Box>
        )}

        {!summary && isFetching && (
          <Typography variant="body1" sx={{ color: neutral[800] }}>
            {COPY.loadingPeople}
          </Typography>
        )}
        {isError && (
          <Typography variant="body1" sx={{ color: neutral[800] }}>
            {COPY.summaryUnavailable}
          </Typography>
        )}

        {countVsPeople && (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {countVsPeople}
          </Typography>
        )}

        {hasPrice && (
          <Box
            sx={{
              background: misc.veryLightGreen,
              border: `1px solid ${misc.darkGreen}`,
              borderRadius: "8px",
              p: 2
            }}
          >
            <Typography
              variant="h5"
              component="p"
              sx={{ color: misc.sunoGreen }}
              data-testid="purchase-licenses-total"
            >
              {isValidCount
                ? COPY.total(count, formatMoney(unitPrice, 2), total)
                : total}
            </Typography>
            {/* Unsaid when the summary could not be read: whether a trial applies is unknown. */}
            {summary && (
              <Typography variant="body2" sx={{ mt: 0.5, color: neutral[800] }}>
                {trialDays
                  ? COPY.trialTerms(trialDays, total)
                  : COPY.chargedToday}
              </Typography>
            )}
          </Box>
        )}

        {purchaseError && (
          <Alert severity="error" data-testid="purchase-licenses-error">
            {purchaseError}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
}
